import React, { ReactElement, useEffect } from 'react';

import { Box, DialogContent, Typography, Grid2 } from '@mui/material';
import { clearBareGoods } from '../../../redux/actions/orderActions';
import { useAppDispatch } from '../../../hooks/hooks';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/reducers';
import { SaveEntityStatus } from '../../../redux/reducers/common';
import { importSites } from '../../../redux/actions/siteActions';
import OrderFormGoods from './OrderFormGoods';
import { importArticles } from '../../../redux/actions/articleActions';
import { filterUniqueBareGoodsByArticle } from '../../../redux/actions/helper';
import {
    clearReturnOrder,
    createNewEmptyReturnOrder,
    importReturnOrder,
    saveReturnOrder,
    setReturnOrderOriginSite,
} from '../../../redux/actions/returnOrderActions';
import { CompleteOrderType } from '../../../common/Types';
import SiteAutocomplete from '../../../common/components/autocomplete/SiteAutocomplete';
import { getSiteOptionString } from '../../../common/components/autocomplete/AutocompleteHelper';
import { SiteType } from '../../../api/ClientApi';

import { FormActions, FormDialogTitle } from '../../../common/components/form/FormPrimitives';

import { ResponsiveDialog } from '../../../common/components/mobile/MobilePrimitives';

type ReturnOrderFormProps = {
    closeForm(): void;
    open: boolean;
    returnOrderId?: number;
};

export default function ReturnOrderForm(props: ReturnOrderFormProps): ReactElement {
    const { open, closeForm, returnOrderId } = props;

    const returnOrder = useSelector((state: RootState) => state.returnOrderForm.returnOrder);
    const saveStatus = useSelector((state: RootState) => state.returnOrderForm.saveStatus);
    const bareGoods = useSelector((state: RootState) => state.goodsToOrderForm.bareGoodsEntities);

    const sites = useSelector((state: RootState) => state.sites.sites);
    const articles = useSelector((state: RootState) => state.articles.articles);

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (open) {
            if (!!returnOrderId && returnOrderId > 0) {
                dispatch(importReturnOrder(returnOrderId));
            } else {
                if (!returnOrder) {
                    dispatch(createNewEmptyReturnOrder());
                }
            }
        }
    }, [dispatch, returnOrderId, open]);

    useEffect(() => {
        if (open) {
            if (!sites) {
                dispatch(importSites());
            }
        }
    }, [dispatch, open]);

    useEffect(() => {
        if (open) {
            if (!articles) {
                dispatch(importArticles());
            }
        }
    }, [dispatch, open]);

    useEffect(() => {
        if (saveStatus === SaveEntityStatus.Saved) {
            handleClose();
        }
    }, [dispatch, saveStatus]);

    const handleSelectOriginSite = (siteId?: number) => {
        const site = sites?.find((s) => s.id === siteId);

        dispatch(setReturnOrderOriginSite(site));
        dispatch(clearBareGoods());
    };

    const handleSave = () => {
        dispatch(saveReturnOrder());
    };

    const handleClose = () => {
        dispatch(clearReturnOrder());
        dispatch(clearBareGoods());
        closeForm();
    };

    const isAvailableQuantityEnough =
        filterUniqueBareGoodsByArticle(bareGoods ?? []).filter((bg) => bg.orderedQuantity > bg.availableQuantity)
            .length <= 0;

    const isOrderDataValid =
        !!returnOrder &&
        !!returnOrder.originSiteId &&
        returnOrder.originSiteId > 0 &&
        !!bareGoods &&
        bareGoods.length > 0 &&
        bareGoods.filter((bg) => !bg.article || bg.orderedQuantity <= 0).length === 0;

    return (
        <ResponsiveDialog scroll="body" maxWidth="md" open={open} onClose={handleClose}>
            <FormDialogTitle>Povratnica - {!returnOrderId ? 'kreiranje' : 'izmena'}</FormDialogTitle>
            <DialogContent dividers sx={{ px: { xs: 2, sm: 5 }, pt: { xs: 3, sm: 5 } }}>
                <Grid2 container spacing={2} style={{ maxWidth: '800px', minWidth: '600px', margin: 'auto' }}>
                    {!returnOrderId ? (
                        <Grid2 size={{ xs: 12, sm: 6 }}>
                            <SiteAutocomplete
                                isRequired
                                selectSite={handleSelectOriginSite}
                                sites={sites?.filter((s) => s.siteType === SiteType.ConstructionSite) ?? []}
                                selectedSiteId={returnOrder?.originSiteId}
                                title="Izvor - skladište"
                            />
                        </Grid2>
                    ) : (
                        <Box>
                            <Typography variant="body1">
                                Izvor - skladište:{' '}
                                <span style={{ fontWeight: 'bold' }}>
                                    {getSiteOptionString(returnOrder?.originSite)}
                                </span>
                            </Typography>
                        </Box>
                    )}
                    <Grid2 size={{ xs: 12 }}>
                        <OrderFormGoods
                            completeOrderType={CompleteOrderType.GeneralReturn}
                            isOriginChosen
                            isReceipt={false}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 0, sm: 12 }} style={{ height: 15, margin: 0, padding: 0 }} />

                    {!isOrderDataValid && (
                        <Grid2 size={{ xs: 12 }}>
                            <Typography color="error">*unesite sva obavezna polja</Typography>
                        </Grid2>
                    )}
                    {!isAvailableQuantityEnough && (
                        <Grid2 size={{ xs: 12 }}>
                            <Typography color="error">*nema dovoljno robe za zahtevani nalog</Typography>
                        </Grid2>
                    )}
                    <FormActions
                        onCancel={handleClose}
                        onSubmit={handleSave}
                        submitLabel="Sačuvaj"
                        disabled={
                            !isOrderDataValid || saveStatus === SaveEntityStatus.Pending || !isAvailableQuantityEnough
                        }
                        pending={saveStatus === SaveEntityStatus.Pending}
                    />
                </Grid2>
            </DialogContent>
        </ResponsiveDialog>
    );
}
